import { useEffect, useState } from "react";
import { Loader2, PlayCircle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, type Lieferart, type JobStatus, type SystemStatus } from "@/lib/api";
import { cn, errText } from "@/lib/utils";
import { useLiveStatus } from "@/hooks/useLiveStatus";

const kindLabel = (k?: string) =>
  k === "full" ? "Komplettabgleich" : k === "delta" ? "Delta-Sync" : "Sync";

export function DashboardPage() {
  const { last, wsState, job: wsJob } = useLiveStatus();
  const [lieferarten, setLieferarten] = useState<Lieferart[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [job, setJob] = useState<JobStatus | null>(null);
  const [systems, setSystems] = useState<SystemStatus[]>([]);

  async function reload() {
    try {
      setLieferarten(await api.getLieferarten());
    } catch (err) {
      toast.error("Lieferarten laden fehlgeschlagen", { description: errText(err) });
    }
  }
  useEffect(() => {
    reload();
  }, []);

  // Job-Status alle 2s pollen — Live-Indikator, ob/was gerade läuft.
  useEffect(() => {
    let alive = true;
    const tick = () => api.getStatus().then((s) => alive && setJob(s)).catch(() => {});
    tick();
    const id = window.setInterval(tick, 2000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  // System-Status (Moodle/Entra) alle 10s pollen — sind echte Netzwerk-Calls.
  useEffect(() => {
    let alive = true;
    const tick = () => api.getSystems().then((s) => alive && setSystems(s)).catch(() => {});
    tick();
    const id = window.setInterval(tick, 10000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  // WS-Job-Events sofort übernehmen (fängt auch kurze Delta-Läufe, die der
  // 2s-Poll verpassen würde).
  useEffect(() => {
    if (wsJob) setJob({ running: wsJob.running, kind: wsJob.kind, started_at: wsJob.started_at });
  }, [wsJob]);

  const running = job?.running ?? false;

  async function fullSync() {
    setSyncing(true);
    try {
      await api.fullSync();
      toast.success("Komplettabgleich gestartet", { description: "Läuft im Hintergrund — siehe Logs." });
    } catch (err) {
      toast.error("Start fehlgeschlagen", { description: errText(err) });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl tracking-tight text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">TCM → Moodle / Entra — Sync-Status</p>
        </div>
        <Button onClick={fullSync} disabled={syncing || running}>
          {syncing || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          Komplettabgleich starten
        </Button>
      </div>

      {/* Live-Job-Indikator */}
      <div
        className={cn(
          "flex items-center gap-3 rounded-md border px-4 py-3 text-sm",
          running
            ? "border-signal-live/40 bg-signal-live/10 text-foreground"
            : "border-border bg-muted/20 text-muted-foreground",
        )}
      >
        {running ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-signal-live" />
            <span>
              <strong>{kindLabel(job?.kind)}</strong> läuft
              {job?.started_at && ` — seit ${new Date(job.started_at).toLocaleTimeString()}`}
            </span>
          </>
        ) : (
          <>
            <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
            <span>Kein Job aktiv</span>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Live-Verbindung" value={wsState === "open" ? "verbunden" : wsState} />
        <Stat
          label="Letzter Delta-Lauf"
          value={last ? new Date(last.time).toLocaleTimeString() : "—"}
          hint={
            last
              ? `Change-Version ${last.version}${last.changed ? " · Änderungen verarbeitet" : " · keine Änderungen"}`
              : "wartet auf ersten Lauf"
          }
        />
        <Stat label="Lieferarten konfiguriert" value={String(lieferarten.length)} />
      </div>

      {/* Angeschlossene Systeme */}
      <Card>
        <CardHeader>
          <CardTitle>Angeschlossene Systeme</CardTitle>
        </CardHeader>
        <CardContent>
          {systems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Status wird geprüft…</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {systems.map((s) => (
                <div key={s.name} className="flex items-start gap-3 rounded-md border border-border p-3">
                  <span
                    className={cn(
                      "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
                      s.ok ? "bg-signal-live shadow-[0_0_8px_-1px] shadow-signal-live" : "bg-destructive",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{s.name}</div>
                    <div className="truncate text-xs text-muted-foreground" title={s.detail}>
                      {s.ok ? s.detail : `Fehler: ${s.detail}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Konfigurierte Lieferarten</CardTitle>
            <Button variant="outline" size="sm" onClick={reload}>
              <RefreshCw className="h-3.5 w-3.5" /> Neu laden
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lieferarten.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Lieferart konfiguriert — unter <strong>Einstellungen</strong> anlegen.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">EDU</th>
                    <th className="py-2 pr-4 font-medium">IdP</th>
                    <th className="py-2 pr-4 font-medium">Vorlauf K/T/Tr (Tage)</th>
                    <th className="py-2 pr-4 font-medium">ab</th>
                  </tr>
                </thead>
                <tbody>
                  {lieferarten.map((l) => (
                    <tr key={l.edu} className="border-t border-border">
                      <td className="py-2 pr-4 font-mono">{l.edu}</td>
                      <td className="py-2 pr-4">{l.identity_provider}</td>
                      <td className="py-2 pr-4">
                        {l.kurs_vorlauf_tage} / {l.teilnehmer_vorlauf_tage} / {l.trainer_vorlauf_tage}
                      </td>
                      <td className="py-2 pr-4">{l.fruehestes_start || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="eyebrow">{label}</div>
        <div className="mt-1 font-serif text-2xl text-foreground">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
